import { useQuery } from '@tanstack/react-query';
import { getInsightsSummary } from '../../api/client';
import { Sparkles, AlertTriangle, AlertCircle, Lightbulb, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const categoryIcons = {
  critical: AlertTriangle,
  warning: AlertCircle,
  opportunity: Lightbulb,
  positive: CheckCircle2,
};

const categoryColors = {
  critical: 'text-red-500',
  warning: 'text-yellow-500',
  opportunity: 'text-blue-500',
  positive: 'text-green-500',
};

function impactScoreColor(score: number): string {
  if (score >= 80) return 'bg-red-100 text-red-700';
  if (score >= 50) return 'bg-orange-100 text-orange-700';
  if (score >= 20) return 'bg-blue-100 text-blue-700';
  return 'bg-green-100 text-green-700';
}

export default function InsightsSummary() {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['insights-summary'],
    queryFn: getInsightsSummary,
  });

  return (
    <div className="card">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-brand-600" />
        <h3 className="text-sm font-semibold text-gray-900">AI Insights</h3>
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
        </div>
      ) : !insights || insights.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">No insights yet. Analysis runs automatically.</p>
      ) : (
        <div className="space-y-3">
          {insights.slice(0, 3).map((insight) => {
            const CategoryIcon = categoryIcons[insight.category] || AlertCircle;
            return (
              <div key={insight.id} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
                <span
                  className={clsx(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                    impactScoreColor(insight.impactScore),
                  )}
                >
                  {insight.impactScore}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                  {insight.title}
                </span>
                <CategoryIcon className={clsx('h-4 w-4 shrink-0', categoryColors[insight.category])} />
              </div>
            );
          })}
        </div>
      )}

      <Link
        to="/insights"
        className="mt-4 block text-center text-sm font-medium text-brand-600 hover:text-brand-700"
      >
        View All Insights &rarr;
      </Link>
    </div>
  );
}
